import { useQueryClient } from "@tanstack/react-query";
import { toLower } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useChangeAppointmentStatusMutation } from "../../../../api/Appointments/changeAppointmentStatus";
import { refetchAllAppointmentsApis } from "../../../../api/Appointments/refetchAllAppointmentsApis";
import clockIcon from "../../../../assets/images/icons/black-clock-01.svg";
import phoneIcon from "../../../../assets/images/icons/black-phone.svg";
import defaultImage from "../../../../assets/images/light-logo.svg";
import { Drawer, GeneralButton, ProfileAvatar } from "../../../../components";
import { EndPoints } from "../../../../Constants/endpoints";
import { useGetData } from "../../../../hooks";
import { IAppointment } from "../../../../interface/appointmentInterfaces";
import { useToast } from "../../../../Services/ToastContext";
import { formatAge } from "../../../../utils/utils";
import AppointmentCancelConfirm from "../AppointmentCancelConfirm/AppointmentCancelConfirm";
import AppointmentStatusTag from "../AppointmentStatusTag/AppointmentStatusTag";
import AppointmentDetailsLoader from "../Loader/AppointmentDetailsLoader";

interface IAppointmentDetailsDrawer {
  item?: IAppointment;
  isOpen?: boolean;
  onClose: () => void;
  isLoading?: boolean;
}

const AppointmentDetailsDrawer: React.FC<IAppointmentDetailsDrawer> = ({
  item: appointment,
  onClose,
  isOpen,
}) => {
  const { showToast } = useToast();
  const { appointmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { data, isLoading } = useGetData<IAppointment>(
    EndPoints.AppointmentDetails,
    {
      enabled: !!appointmentId,
    },
    appointmentId,
  );
  const item = appointment ?? data?.Data;

  const [isCancelConfirmOpen, setIsCancelConfirmOpen] =
    useState<boolean>(false);

  const { mutate, isPending } = useChangeAppointmentStatusMutation({
    onSuccess: () => {
      setIsCancelConfirmOpen(false);
      if (confirmCancelStatus == "Reject" || confirmCancelStatus == "Cancel") {
        showToast(
          t(
            confirmCancelStatus == "Reject"
              ? "appointment_rejected_successfully"
              : "appointment_canceled_successfully",
          ),
          "success",
        );
        setConfirmCancelStatus(undefined);
      } else showToast(t("appointment_approved_successfully"), "success");
    },
    onError: (error) => {
      showToast(error.message, "error");
    },
  });

  const [confirmCancelStatus, setConfirmCancelStatus] = useState<
    "Reject" | "Cancel"
  >();

  const isRelative = item?.Relative;

  const queryClient = useQueryClient();
  const isPastDay = moment(item?.Date, "MM/DD/YYYY h:mm:ss A").isBefore(
    moment(),
  ); 
  const onSubmit = (values: { Note: string }) => {
    mutate(
      {
        AppointmentId: item?.Id,
        action: confirmCancelStatus,
        Note: values?.Note,
      } as any,
      {
        onSuccess: () => {
          setIsCancelConfirmOpen(false);
          refetchAllAppointmentsApis(queryClient);
        },
        onError: (error) => {
          showToast(error.message, "error");
        },
      },
    );
  };

  useEffect(() => {
    if (location.state === "reject") {
      setConfirmCancelStatus("Reject");
      setIsCancelConfirmOpen(true);
    }
  }, [appointmentId, item?.Id]);

  const appointmentDate = moment(item?.Date);

  return (
    <>
      <Drawer
        isOpen={isOpen || (!!appointmentId && appointmentId == item?.Id)}
        customStyle="w-[402px]"
        header={t("appointment_details")}
        onClose={onClose}
      >
        {isLoading ? (
          <AppointmentDetailsLoader />
        ) : (
          <>
            <div className="flex flex-col gap-6 rounded-lg border border-primary-click bg-input-field p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="appointment-date flex items-center gap-2 border-e-2 border-primary-bg pe-6 text-center text-body-text">
                  <div className="text-3xl text-primary-text">
                    {appointmentDate.format("DD")}
                  </div>
                  <div>
                    <div className="text-xs">
                      {appointmentDate.format("dddd").toUpperCase()}
                    </div>

                    <div className="text-xs">
                      {appointmentDate.format("MMMM").toUpperCase()}
                    </div>
                  </div>
                </div>
                <AppointmentStatusTag statusCode={item?.Status} />
              </div>
              <hr />
              {item?.Relative && (
                <div className="flex flex-col gap-2">
                  <div className="font-bold text-primary-text">
                    <span>{t("relative_for")}</span>
                    <span> - </span>
                    <span>{item?.Account?.FullName}</span>
                  </div>
                  {item?.Account?.RelationType && (
                    <div className="text-xs text-primary-text">
                      <span>{t("relative_relationship")}: </span>
                      <span>{item?.Account?.RelationType}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center gap-3">
                <ProfileAvatar
                  Id={
                    item?.Relative?.Id ? item?.Relative?.Id : item?.Account?.Id
                  }
                  FullName={
                    item?.Relative?.FullName
                      ? item?.Relative?.FullName
                      : item?.Account?.FullName
                  }
                  src={
                    item?.Relative?.Image
                      ? item?.Relative?.Image
                      : item?.Account?.Image
                  }
                  size="very-large"
                  imageToLoad={defaultImage}
                  alt="doctor"
                  className="h-12 w-12 rounded-full object-cover"
                />
                <div className="flex flex-col">
                  <p className="font-bold text-primary-text">
                    {item?.Relative?.FullName
                      ? item?.Relative?.FullName
                      : item?.Account?.FullName}
                  </p>
                  <div className="flex items-center gap-1 text-sm text-body-text">
                    <p>
                      {!!(isRelative
                        ? item?.Relative?.Birthday
                        : item?.Account?.Birthday)
                        ? formatAge(
                            isRelative
                              ? item?.Relative?.Birthday
                              : item?.Account?.Birthday,
                          ) +
                          " " +
                          t("years_old")
                        : undefined}
                    </p>
                    <p>•</p>
                    <p>
                      {isRelative
                        ? t(toLower(item?.Relative?.Gender))
                        : t(toLower(item?.Account?.Gender))}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1 text-sm">
                <div className="flex items-center gap-2">
                  <img src={clockIcon} alt="time" />
                  {appointmentDate.format("hh:mm")} -{" "}
                  {appointmentDate
                    .add(item?.Duration, "minutes")
                    .format("hh:mm A")}
                </div>
                {item?.Account?.PhoneNumber && (
                  <div className="flex items-center gap-2">
                    <img src={phoneIcon} alt="phone number" />
                    <span dir="ltr">{item?.Account?.PhoneNumber}</span>
                  </div>
                )}
              </div>
              {item?.Note && (
                <div className="flex flex-col gap-2 rounded-lg bg-error-00 p-3 text-sm">
                  <span className="font-bold text-error">
                    {t("appointment_cancel_reason")}
                  </span>
                  <span>{item?.Note}</span>
                </div>
              )}
            </div>
            {(isOpen || (!!appointmentId && appointmentId == item?.Id)) &&
              !isPastDay &&
              item?.Status != "CANCELED" &&
              item?.Status != "REJECTED" && (
                <div className="flex flex-col gap-2">
                  {item?.Status == "APPROVED" || !item?.HasAction ? (
                    <div className="flex items-center gap-1">
                      <GeneralButton
                        title={t("emergency_cancel")}
                        color="red"
                        className="!w-1/2"
                        onClick={() => {
                          setIsCancelConfirmOpen(true);
                          setConfirmCancelStatus("Cancel");
                        }}
                      />
                      <GeneralButton
                        title={t("view_profile")}
                        className="!w-1/2"
                        onClick={() => {
                          navigate(
                            item?.Relative?.Id
                              ? `/patients_list/${item?.Relative?.Id}/medical-records`
                              : `/patients_list/${item?.Account?.Id}/medical-records`,
                          );
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      <GeneralButton
                        title={t("reject")}
                        color="lite"
                        className="!h-[46px] !rounded-lg !bg-error-00 !text-error"
                        onClick={() => {
                          setIsCancelConfirmOpen(true);
                          setConfirmCancelStatus("Reject");
                        }}
                      />
                      <GeneralButton
                        className="!h-[46px] !rounded-lg border border-primary"
                        title={t("confirm")}
                        color="blue"
                        onClick={() => {
                          mutate({
                            AppointmentId: item?.Id,
                            action: "Approve",
                          } as any);
                        }}
                      />
                    </>
                  )}
                </div>
              )}
          </>
        )}
      </Drawer>

      {isCancelConfirmOpen && (
        <AppointmentCancelConfirm
          status={confirmCancelStatus}
          isOpen={isCancelConfirmOpen}
          onClose={() => {
            setIsCancelConfirmOpen(false);
          }}
          onSubmit={onSubmit}
          isLoading={isPending}
        />
      )}
    </>
  );
};

export default AppointmentDetailsDrawer;
